import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppConfig } from '@state';
import { useSystem } from '@ohif/core';
import PageLayout from '../components/layouts/PageLayout';
import {
  PeerReviewPagination,
  PeerReviewStats,
  PeerReviewTable,
  PeerReviewToolbar,
} from '../components/peerReview';
import '../components/peerReview/peerReview.css';
import { getStudyViewerHref } from '../components/worklist';
import { resolvePeerReviewViewerStudyUid } from '../components/peerReview/resolvePeerReviewViewerStudyUid';
import {
  loadPeerReviewColumnVisibility,
  persistPeerReviewColumnVisibility,
  type PeerReviewColumnKey,
} from '../constants/peerReviewColumns';
import {
  getPeerReviewListTotal,
  getPeerReviewStudyFields,
  getPeerReviewSummary,
  listPeerReviews,
  type PeerReview,
  type PeerReviewSummary,
} from '../services/peerReviewService';
import {
  isPeerReviewUrgent,
  dedupePeerReviewsByStudy,
} from '../components/peerReview/peerReviewUtils';
import { authService } from '../auth/authService';
import { canAccessPage, canPerformPeerReview } from '../utils/permissions';
import { getDefaultRoute } from '../utils/getDefaultRoute';
import { preserveQueryParameters } from '../../utils/preserveQueryParameters';

const BREADCRUMBS = [
  { label: 'Dashboard', path: '/' },
  { label: 'RIS', path: '/' },
  { label: 'Peer Review' },
];

const SEARCH_DEBOUNCE_MS = 300;

function summarizeFromRows(rows: PeerReview[]): PeerReviewSummary {
  let urgent = 0;
  let routine = 0;
  let inReview = 0;

  for (const row of rows) {
    if (String(row.status).toLowerCase() === 'assigned') {
      inReview += 1;
    }
    const study = getPeerReviewStudyFields(row);
    if (isPeerReviewUrgent(study.priority, study.is_critical)) {
      urgent += 1;
    } else {
      routine += 1;
    }
  }

  return {
    in_review: inReview,
    urgent,
    routine,
  };
}

export default function PeerReviewPage() {
  const [appConfig] = useAppConfig();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { servicesManager } = useSystem();
  const uiNotificationService = servicesManager?.services?.uiNotificationService;
  const user = authService.getUser();
  const viewOnly = !canPerformPeerReview(user);
  const showExhausted = searchParams.get('needs_action') === 'exhausted';
  const workflowIdParam = Number(searchParams.get('workflow_id'));
  const exhaustedWorkflowId =
    Number.isInteger(workflowIdParam) && workflowIdParam > 0 ? workflowIdParam : undefined;
  const exhaustedStudyUid = searchParams.get('study_instance_uid')?.trim() || undefined;

  const [rows, setRows] = useState<PeerReview[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<PeerReviewSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingReviewId, setOpeningReviewId] = useState<number | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [modality, setModality] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [columnVisibility, setColumnVisibility] = useState<Record<PeerReviewColumnKey, boolean>>(
    () => loadPeerReviewColumnVisibility()
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listResult, summaryResult] = await Promise.all([
        listPeerReviews({
          // Exhausted chains have no Submitted row — latest open/timed-out assignment.
          // Otherwise keep finished reviews on the worklist after submit.
          status: showExhausted ? 'Assigned,Expired' : 'Assigned,Submitted',
          search: search || undefined,
          modality: modality || undefined,
          needs_action: showExhausted ? 'exhausted' : undefined,
          workflow_id: showExhausted ? exhaustedWorkflowId : undefined,
          study_instance_uid: showExhausted ? exhaustedStudyUid : undefined,
          page,
          limit: perPage,
        }),
        getPeerReviewSummary().catch(() => null),
      ]);

      const rawRows = listResult.peerReviews;
      // Admin / technician: one worklist row per study (multi-level assignments collapse).
      const displayRows = viewOnly ? dedupePeerReviewsByStudy(rawRows) : rawRows;
      const apiTotal = getPeerReviewListTotal(listResult.pagination, rawRows.length);
      const duplicateCount = rawRows.length - displayRows.length;

      setRows(displayRows);
      setTotal(Math.max(displayRows.length, apiTotal - Math.max(0, duplicateCount)));
      setSummary(
        viewOnly
          ? summarizeFromRows(displayRows)
          : (summaryResult ?? listResult.summary ?? summarizeFromRows(displayRows))
      );
      setSelectedIds(new Set());
    } catch (err) {
      setRows([]);
      setTotal(0);
      setSummary(null);
      setError(err instanceof Error ? err.message : 'Failed to load peer reviews');
    } finally {
      setLoading(false);
    }
  }, [
    search,
    modality,
    page,
    perPage,
    viewOnly,
    showExhausted,
    exhaustedWorkflowId,
    exhaustedStudyUid,
  ]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const stats = useMemo(
    () => ({
      inReview: summary?.in_review ?? 0,
      urgent: summary?.urgent ?? 0,
      routine: summary?.routine ?? 0,
    }),
    [summary]
  );

  const handleColumnVisibilityChange = useCallback((next: Record<PeerReviewColumnKey, boolean>) => {
    setColumnVisibility(next);
    persistPeerReviewColumnVisibility(next);
  }, []);

  const clearNeedsActionFilter = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('needs_action');
    next.delete('workflow_id');
    next.delete('study_instance_uid');
    setSearchParams(next, { replace: true });
    setPage(1);
  }, [searchParams, setSearchParams]);

  const handleToggleRow = useCallback((id: number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleToggleAllPage = useCallback(
    (checked: boolean) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        rows.forEach(row => {
          if (checked) {
            next.add(row.id);
          } else {
            next.delete(row.id);
          }
        });
        return next;
      });
    },
    [rows]
  );

  const handleReview = useCallback(
    async (row: PeerReview) => {
      setOpeningReviewId(row.id);
      try {
        // Same gate as Studies: only open a StudyInstanceUID that QIDO can find
        // (PanelStudyBrowser redirects to /notfoundstudy otherwise).
        const resolved = await resolvePeerReviewViewerStudyUid(row);
        const uid = resolved?.studyInstanceUid?.trim();
        if (!uid) {
          uiNotificationService?.show({
            title: 'Study not available',
            message:
              'One or more of the requested studies are not available at this time. Check Study Instance UID / accession on the peer review.',
            type: 'error',
            duration: 8000,
          });
          return;
        }

        const fields = getPeerReviewStudyFields(row);
        console.info(
          'Peer review viewer: resolved StudyInstanceUID via',
          resolved.resolvedVia,
          uid
        );

        // Same launcher as Imaging Worklist (getStudyViewerHref → /viewer?StudyInstanceUIDs=…).
        const href = getStudyViewerHref({
          study: {
            studyInstanceUid: uid,
            modalities: resolved.modalities || fields.modality || '',
          },
          appConfig,
          dataPath: '',
          filterValues: {},
          preserveQueryParameters,
        });

        const target = href
          ? href.startsWith('/')
            ? href
            : `/${href}`
          : (() => {
              const query = new URLSearchParams();
              query.append('StudyInstanceUIDs', uid);
              preserveQueryParameters(query);
              return `/viewer?${query.toString()}`;
            })();

        // Carry peer review id so ReportPanel enters peer-assessment mode.
        // returnTo keeps viewer Back on the Peer Review worklist (not Studies).
        const withPeerReview = new URL(target, window.location.origin);
        withPeerReview.searchParams.set('peerReviewId', String(row.id));
        withPeerReview.searchParams.set('returnTo', '/peer-reviews');
        navigate(`${withPeerReview.pathname}${withPeerReview.search}`);
      } catch (err) {
        console.error('Peer review Review open failed:', err);
        uiNotificationService?.show({
          title: 'Unable to open study',
          message: err instanceof Error ? err.message : 'Failed to open viewer for peer review',
          type: 'error',
          duration: 8000,
        });
      } finally {
        setOpeningReviewId(null);
      }
    },
    [appConfig, navigate, uiNotificationService]
  );

  if (!canAccessPage(user, '/peer-reviews')) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  return (
    <PageLayout
      title="Peer Review"
      hideTitleInHeader
      headerVariant="light"
      breadcrumbs={BREADCRUMBS}
      contentClassName="peer-review-page-layout-content"
    >
      <div className="peer-review-page">
        <h1 className="peer-review-page__title">Peer review</h1>

        {showExhausted ? (
          <div className="peer-review-needs-action">
            <span>
              Showing peer review chains that ran out of reviewers and need a manual reassignment.
            </span>
            <button
              type="button"
              className="peer-review-needs-action__clear"
              onClick={clearNeedsActionFilter}
            >
              Show all peer reviews
            </button>
          </div>
        ) : null}

        <PeerReviewStats
          stats={stats}
          loading={loading && !summary}
        />

        {error ? <p className="peer-review-error">{error}</p> : null}

        <div className="peer-review-sheet">
          <PeerReviewToolbar
            search={searchInput}
            onSearchChange={setSearchInput}
            modality={modality}
            onModalityChange={value => {
              setModality(value);
              setPage(1);
            }}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
          />

          {loading && rows.length === 0 ? (
            <div className="peer-review-loading">Loading peer reviews…</div>
          ) : (
            <PeerReviewTable
              rows={rows}
              columnVisibility={columnVisibility}
              selectedIds={selectedIds}
              onToggleRow={handleToggleRow}
              onToggleAllPage={handleToggleAllPage}
              onReview={handleReview}
              openingReviewId={openingReviewId}
              viewOnly={viewOnly}
            />
          )}

          <PeerReviewPagination
            selectedCount={selectedIds.size}
            totalItems={total}
            currentPage={page}
            perPage={perPage}
            onChangePage={setPage}
            onChangePerPage={next => {
              setPerPage(next);
              setPage(1);
            }}
          />
        </div>
      </div>
    </PageLayout>
  );
}
